<template>
  <v-row no-gutters class="pt-5 pr-2">
    <v-col v-if="computedIsFetched">
      <card-title-row :straight-title="additionalModuleName">
        <template #buttons>
          <base-rounded-button
            v-if="permissionsFolders.main_value > 1"
            icon="mdi-plus"
            size="x-small"
            color="success"
            :tooltip="$i18n.t('button.addFolder')"
            @click="addFolder"
          />
        </template>
      </card-title-row>
      <card-title-row
        is-subtitle
        translation-path="sidebar.visualisationOnMap"
        :amount="1"
        :is-plural="true"
        class="pt-2"
      />
      <sidebar-row class="pt-2">
        <sidebar-select-row
          translation-path="sidebar.visualizationType"
          font-size="14px"
          @input="handleStyleChange"
          :value="currentStyle"
          :select-props="styleSelectProps"
        ></sidebar-select-row>
      </sidebar-row>
      <module-legend ref="moduleLegend" border-bottom module-name="folders" />
      <sidebar-row border-bottom>
        <sidebar-summary :items="foldersSummaryItems" class="my-4">
          <template v-for="item of foldersSummaryItems" #[item.name]>
            {{ item.length }}
          </template>
        </sidebar-summary>
      </sidebar-row>
      <sidebar-row class="pt-4">
        <v-col cols="9" class="pa-0">
          <data-input
            :data-type="{ name: 'text' }"
            id="searchField"
            append-icon="mdi-magnify"
            :hint="$i18n.t('sidebar.foldersSearch')"
            persistent-hint
            clearable
            v-model="search"
            @keydown.enter="findData"
            class="align-center justify-center"
          ></data-input>
        </v-col>
      </sidebar-row>
      <main-loader v-if="isSearching"></main-loader>
      <sidebar-row v-else>
        <v-list>
          <v-list-item
            v-for="doc in computedData"
            :key="doc.id"
            @click="openFolder(doc.folder_id)"
            :ripple="false"
            class="pa-0 mb-5 folders-list"
          >
            <v-list-item-content class="ma-0 pa-0">
              <sidebar-row :has-icon-column="false">
                <document-preview :doc="doc" @download="downloadFile($event)" class="folder-element"></document-preview>
              </sidebar-row>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </sidebar-row>
    </v-col>
    <main-loader v-else></main-loader>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';
import { getIconColorByFileType, getIconByFileType } from '@/assets/js/utility';

import CardTitleRow from '@/components/CardTitleRow';
import SidebarRow from '@/components/SidebarRow';
import SidebarSelectRow from '@/components/SidebarSelectRow';
import MainLoader from '@/components/MainLoader';
import SidebarSummary from '@/components/SidebarSummary';
import DocumentPreview from '@/components/DocumentPreview';
import ModuleLegend from '@/components/ModuleLegend';
import DataInput from '@/components/DataInput';

export default {
  name: 'Folders',
  components: {
    DataInput,
    ModuleLegend,
    DocumentPreview,
    MainLoader,
    SidebarSelectRow,
    SidebarRow,
    CardTitleRow,
    SidebarSummary,
  },
  computed: {
    additionalModuleName: get('admin/additionalModules@FOLDERS.verbose_name'),
    permissions: get('users/toolsPermissions'),
    dataSourceName: get('admin/modulesMapping@folders.datasource_name'),
    styles: get('styles/styles@:dataSourceName'),
    currentStyle: get('styles/currentStyle@:dataSourceName'),
    defaultStyle: get('styles/defaultStyle@:dataSourceName'),
    loaded: get('modules/loaded@:dataSourceName'),

    computedIsFetched() {
      return this.isFetched && this.loaded;
    },
    foldersSummaryItems() {
      return [
        {
          name: 'folders',
          translationPath: 'dialog.folders',
          color: 'rgba(0,145,234)',
          length: this.getItemsLength('folders'),
        },
        {
          name: 'documents',
          translationPath: 'dialog.folders_objects',
          color: 'rgba(0,145,234)',
          length: this.getItemsLength('documents'),
        },
      ];
    },
    permissionsFolders() {
      return this.permissions['FOLDERS'];
    },
    computedSelectStyles() {
      return [this.defaultStyle, ...this.$_sortObjectsByAttribute([...this.styles], 'name')];
    },
    styleSelectProps() {
      return {
        items: this.computedSelectStyles,
        itemText: 'name',
        itemValue: 'id',
        returnObject: true,
      };
    },
    computedData() {
      if (this.foundDocuments.length > 0) {
        return this.foundDocuments.map(item => {
          return {
            ...item,
            folderIdText: this.previewIdText(item.folder_id),
            documentIdText: this.previewIdText(item.document_id),
            addedText: this.previewAddedText(item.document_added_by_name, item.document_added_at),
            icon: getIconByFileType(item.document_filename),
            iconColor: getIconColorByFileType(item.document_filename),
          };
        });
      } else {
        return [];
      }
    },
  },
  data: () => ({
    search: '',
    isLoading: false,
    isFetched: false,
    foundDocuments: [],
    folderStats: undefined,
    isSearching: false,
  }),
  methods: {
    getFolders: call('layers/getFolders'),
    getStyles: call('styles/getStyles'),
    getStyle: call('styles/getStyle'),
    setCurrentStyle: call('styles/setCurrentStyle'),
    findDocuments: call('layers/findDocuments'),
    downloadDocument: call('layers/downloadDocument'),
    stats: call('layers/getFolderSummary'),

    async openFolder(id) {
      await this.$router.push({
        name: 'folder',
        params: { fid: id },
      });
      this.foundDocuments = [];
    },
    async findData() {
      this.isSearching = true;
      this.foundDocuments = await this.findDocuments(this.search);
      this.isSearching = false;
      if (this.foundDocuments.length === 0) {
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.documentsNotFound', { name: this.search }),
        });
      }
      this.search = '';
    },
    async downloadFile(id) {
      await this.downloadDocument(id);
    },
    previewIdText(id) {
      return `[ID: ${id}]`;
    },
    previewAddedText(owner, date) {
      return `${this.$dayjs(date).format('L')}, ${owner}`;
    },
    getItemsLength(name) {
      if (name === 'folders') {
        return this.folderStats.folders_count;
      } else {
        return this.folderStats.documents_count;
      }
    },
    async handleStyleChange(style) {
      await this.setCurrentStyle({ dataSource: this.dataSourceName, style: style });
      this.$root.$emit('folderStyleChanged', style);
      await this.$refs.moduleLegend.getModuleStyle();
    },
    async addFolder() {
      await this.$router.push({
        name: 'folder',
        params: { fid: 'new' },
      });
    },
    async init() {
      await Promise.all([this.getFolders(), this.getStyles(this.dataSourceName)]);
      this.folderStats = await this.stats();
      this.isFetched = true;
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped lang="scss">
.border-bottom {
  @include border(bottom);
}
.folder-element:hover {
  background-color: rgba(188, 188, 188, 0.2);
}
.folders-list {
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25), 0px -1px 0px rgba(0, 0, 0, 0.15) !important;
}
</style>
